import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth, authHeaders } from "../../lib/auth";
import StatusPill from "../dashboard/StatusPill";

const SECTION_CLS = "border border-white/10 -mt-px p-5";

export default function CoachDashboard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/coach/orders", { headers: authHeaders(token) })
      .then((r) => {
        // Backend contract is { orders: [...], earnings: number }. Handle every
        // legacy shape defensively so a malformed response never blanks the page.
        const raw = r.data;
        const orders = Array.isArray(raw) ? raw : (Array.isArray(raw?.orders) ? raw.orders : []);
        const earningsNum = Number(raw?.earnings);
        const earnings = Number.isFinite(earningsNum) ? earningsNum : 0;
        setData({ orders, earnings });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Could not load your assigned orders. Please refresh.");
        setData({ orders: [], earnings: 0 });
      });
  }, [token]);

  if (error && !data) return <div className="p-12 text-white/60" data-testid="coach-error">{error}</div>;
  if (!data) return <div className="p-12 text-white/40" data-testid="coach-loading">Loading…</div>;

  const orders = Array.isArray(data.orders) ? data.orders : [];
  const earnings = Number.isFinite(Number(data.earnings)) ? Number(data.earnings) : 0;
  const waiting = orders.filter((o) => ["awaiting_upload", "in_progress"].includes(o?.workspace_status));
  const toReview = orders.filter((o) => ["submitted", "in_review"].includes(o?.workspace_status));
  const completed = orders.filter((o) => o?.workspace_status === "completed");

  return (
    <div className="px-6 md:px-12 py-12 max-w-6xl">
      <div className="text-[11px] tracking-[0.32em] uppercase text-[#2A5BFF]">Coach</div>
      <h1 className="font-anton uppercase text-5xl md:text-6xl leading-[0.9]" data-testid="coach-heading">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 mt-8" data-testid="coach-stats">
        <Stat label="Waiting on customer" value={waiting.length} testid="coach-stat-waiting" />
        <Stat label="Ready to review" value={toReview.length} testid="coach-stat-to-review" />
        <Stat label="Completed" value={completed.length} testid="coach-stat-completed" />
        <Stat label="Earnings" value={`£${earnings.toFixed(2)}`} accent testid="coach-stat-earnings" />
      </div>

      <Section title="Ready to Review" testid="coach-list-to-review">
        {toReview.length === 0 && <div className="p-5 text-white/40 text-sm">Nothing to review right now.</div>}
        {toReview.map((o) => (
          <Card key={o.id} order={o}>
            <Link to={`/coach/orders/${o.id}`} className="btn-primary px-4 py-2 text-[11px] tracking-[0.24em] uppercase" data-testid={`open-${o.id}`}>Open</Link>
          </Card>
        ))}
      </Section>

      <Section title="Waiting on Customer" testid="coach-list-waiting">
        {waiting.length === 0 && <div className="p-5 text-white/40 text-sm">No orders waiting on a customer upload.</div>}
        {waiting.map((o) => (
          <Card key={o.id} order={o}>
            <span className="text-[10px] tracking-[0.24em] uppercase text-white/40">Awaiting upload</span>
          </Card>
        ))}
      </Section>

      <Section title="Completed" testid="coach-list-completed">
        {completed.length === 0 && <div className="p-5 text-white/40 text-sm">No completed reviews yet.</div>}
        {completed.map((o) => (
          <Card key={o.id} order={o}>
            <Link to={`/coach/orders/${o.id}`} className="btn-ghost px-4 py-2 text-[11px] tracking-[0.24em] uppercase">View</Link>
          </Card>
        ))}
      </Section>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className={`p-5 border border-white/10 ${accent ? "bg-[#2A5BFF]/[0.06]" : ""}`} data-testid={testid}>
      <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{label}</div>
      <div className={`font-anton text-4xl mt-3 leading-none ${accent ? "text-[#2A5BFF]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-anton uppercase text-2xl mb-4">{title}</h2>
      <div data-testid={testid}>{children}</div>
    </section>
  );
}

function Card({ order, children }) {
  return (
    <div className={SECTION_CLS + " flex items-start justify-between gap-4 flex-wrap"} data-testid={`coach-order-${order.id}`}>
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">
          {order.id.slice(0, 8)} · {new Date(order.created_at).toLocaleDateString()}
        </div>
        <div className="font-anton uppercase text-2xl mt-1 leading-none">{order.service_title}</div>
        <div className="mt-2 flex flex-wrap gap-3 text-[10px] tracking-[0.22em] uppercase text-white/60">
          <span>Customer · {order.customer_first_name || "—"}</span>
          <span>{order.is_express ? "Express" : "Standard"}</span>
          <span>ETA · {order.estimated_turnaround || "—"}</span>
          <span>Payout · £{order.coach_payout?.toFixed(2)}</span>
          {order.submitted_at && <span>Submitted · {new Date(order.submitted_at).toLocaleDateString()}</span>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <StatusPill status={order.workspace_status} />
        {children}
      </div>
    </div>
  );
}
